<template>
  <div class="vehicle-dashboard">
    <!-- Header Section -->
    <div class="dashboard-header d-flex justify-content-between align-items-start mb-4">
      <div class="dashboard-header-left">
        <h2 class="text-primary font-weight-bold mb-0">Vehicle Performance Dashboard</h2>
        <p class="text-muted small" v-if="!selectedVehicleRegNo">Advanced Fleet Analytics & Intelligence</p>
        <div v-else class="d-flex align-items-center mt-1">
          <b-badge 
            variant="primary" pill class="px-3 py-2 mr-2 shadow-sm"
            style="cursor: pointer"
            v-b-tooltip.hover title="View Technical Details"
            @click="onOpenVehicleMasterDetails({ vehicleTruckId: selectedVehicleId, regNo: selectedVehicleRegNo })"
          >
             <md-icon size="16" class="mr-1 text-white">local_shipping</md-icon> 
             Vehicle: {{ selectedVehicleRegNo }}
          </b-badge>
          <b-button variant="outline-secondary" size="sm" pill @click="clearVehicleSelection" class="shadow-sm">
            <md-icon size="14">arrow_back</md-icon> Back to Fleet Overview
          </b-button>
        </div>
        
        <!-- Advanced Ticker -->
        <div class="d-flex mt-2 stats-ticker align-items-center">
           <div class="ticker-item mr-4">
              <span class="text-muted xs-font">LIVE DRIVERS:</span> <span class="text-primary font-weight-bold">38/42</span>
           </div>
           <div class="ticker-item mr-4 d-flex align-items-center">
              <span class="text-muted xs-font mr-2">FLEET STATUS:</span>
              <div class="mini-gauge-container" v-b-tooltip.hover :title="summaryData ? `Active: ${summaryData.activeVehicles}, Inactive: ${summaryData.inactiveVehicles}` : 'Active: 32, Inactive: 10'">
                 <apexchart type="radialBar" height="40" width="40" :options="miniFleetOptions" :series="summaryData ? [summaryData.activePercentage] : [76]"></apexchart>
              </div>
           </div>
           <div class="ticker-item">
              <span class="text-muted xs-font">FLEET HEALTH:</span> <span :class="['font-weight-bold', summaryData && summaryData.fleetStatus === 'WARNING' ? 'text-warning' : 'text-success']">{{ summaryData ? summaryData.fleetStatus : 'OPTIMAL' }}</span>
           </div>
        </div>
      </div>
      <div class="dashboard-controls d-flex align-items-end">
        <b-form-group label="Date Range" class="mb-0 mr-3 dashboard-date-group">
          <date-range-picker
            v-model="dateRange"
            :locale-data="locale"
            :auto-apply="true"
            class="glass-input"
            @update="onDateRangeSelected"
          ></date-range-picker>
        </b-form-group>
        <b-form-group label="Vehicle Search" class="mb-0 mr-3" style="min-width: 250px;">
          <model-list-select 
            :list="$store.getters['commonDropdownMaster/getAllVehicleDDList']" 
            v-model="selectedVehicleId"
            option-value="value" 
            option-text="label" 
            placeholder="Select Vehicle No"
            @input="refreshData"
          ></model-list-select>
        </b-form-group>
        <b-button variant="primary" class="shadow-sm px-4 dashboard-refresh-btn" @click="refreshData">
          <md-icon>refresh</md-icon>
        </b-button>
      </div>
    </div>

    <!-- KPI Summary Cards -->
    <b-row class="mb-4">
      <b-col md="3" v-for="(kpi, index) in filteredKpiSummary" :key="index">
        <kpi-card v-bind="kpi" />
      </b-col>
    </b-row>

    <!-- Fleet Status Tabs -->
    <!-- <b-row class="mb-4">
     
    </b-row> -->

    <!-- Main Content Area -->
    <b-row>
      <b-col lg="12">
        <b-card no-body shadow-sm id="details-section">
          <b-tabs v-model="activeTab" pills card active-nav-item-class="bg-primary text-white shadow" @input="handleTabChange">
            <b-tab title="Utilization & Trips" active>
              <utilization-tab 
                :items="filteredVehicleUtilization" 
                :fields="utilizationFields" 
                :chart-options="utilizationChartOptions" 
                :chart-series="utilizationChartSeries"
                @select-vehicle="selectVehicle"
                @open-trip-details="onUtilizationTripDrilldown"
                @open-assignment-details="onUtilizationAssignmentDrilldown"
                @open-master-details="onOpenVehicleMasterDetails"
                :view-mode="viewMode"
                @update:viewMode="viewMode = $event"
                :vehicle-filter="vehicleFilter"
                @update:vehicleFilter="vehicleFilter = $event"
                :top-n="utilizationTopN"
                @update:topN="utilizationTopN = $event"
                :show-export="true"
                :is-exporting="isUtilizationExporting"
                @export="exportUtilizationToExcel"
                :loading="isSummaryLoading"
              />
            </b-tab>

            <b-tab title="Operations Intelligence">
              <operations-intelligence-tab 
                :trip-volume-series="tripVolumeSeries"
                :trip-volume-options="tripVolumeOptions"
                :availability-series="availabilitySeries"
                :availability-options="availabilityOptions"
                :vehicle-trips-series="vehicleTripsSeries"
                :vehicle-trips-options="vehicleTripsOptions"
                :fuel-trend-series="fuelTrendSeries"
                :fuel-trend-options="fuelTrendOptions"
                :audit-stats="accountingAuditStats"
                :volume-loading="isTripVolumeLoading"
                :availability-loading="isAvailabilityLoading"
                :activity-loading="isVehicleActivityLoading"
                :audit-loading="isAccountingAuditLoading"
                :fuel-loading="isFuelEfficiencyLoading"
              />
            </b-tab>

            <b-tab title="Location Intelligence">
              <location-intelligence-tab 
                :trip-trends-series="tripTrendsSeries"
                :trip-trends-options="tripTrendsOptions"
                :density-data="locationDensity"
                :trip-loading="isLocationTripLoading"
                :density-loading="isLocationDensityLoading"
                :initial-role="selectedLocationRole"
                @role-change="onLocationRoleChanged"
              />
            </b-tab>

            <b-tab title="Financial Analytics">
              <financial-analytics-tab
                :client-series="clientRevenueSeries"
                :client-options="clientRevenueOptions"
                :vehicle-series="vehicleRevenueSeries"
                :vehicle-options="vehicleRevenueOptions"
                :shipping-line-series="slRevenueSeries"
                :shipping-line-options="slRevenueOptions"
                :monthly-revenue-series="monthlyRevenueSeries"
                :monthly-revenue-options="monthlyRevenueOptions"
                :yearly-revenue-series="yearlyRevenueSeries"
                :yearly-revenue-options="yearlyRevenueOptions"
                :client-raw-data="clientRevenueRaw"
                :vehicle-raw-data="vehicleRevenueRaw"
                :sl-raw-data="slRevenueRaw"
                :monthly-raw-data="monthlyRevenueRaw"
                :yearly-raw-data="yearlyRevenueRaw"
                :client-expense-series="clientExpenseSeries"
                :client-expense-options="clientExpenseOptions"
                :vehicle-expense-series="vehicleExpenseSeries"
                :vehicle-expense-options="vehicleExpenseOptions"
                :shipping-line-expense-series="slExpenseSeries"
                :shipping-line-expense-options="slExpenseOptions"
                :monthly-expense-series="monthlyExpenseSeries"
                :monthly-expense-options="monthlyExpenseOptions"
                :yearly-expense-series="yearlyExpenseSeries"
                :yearly-options="yearlyExpenseOptions"
                :client-expense-raw="clientExpenseRaw"
                :vehicle-expense-raw="vehicleExpenseRaw"
                :loading="isFinanceLoading"
                :sl-expense-raw="slExpenseRaw"
                :monthly-expense-raw="monthlyExpenseRaw"
                :yearly-expense-raw="yearlyExpenseRaw"
              />
            </b-tab>

            <b-tab title="Maintenance & Health">
              <maintenance-health-tab 
                v-if="activeTab === 4"
                :items="maintenanceHistory"
                :item-brief="maintenanceItemBrief"
                :loading="isMaintenanceLoading"
                :brief-loading="isItemBriefLoading"
                :selected-vehicle-id="selectedVehicleId"
                @select-vehicle="selectVehicle"
              />
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>

      <!-- Charts Section -->
      <b-col lg="12">
        <dashboard-chart 
          title="Revenue vs Expense Trend" 
          type="area" 
          height="350" 
          :options="chartOptions" 
          :series="filteredChartSeries"
        >
          <template #header-right>
            <b-form-radio-group v-model="chartPeriod" :options="['Monthly', 'Weekly']" buttons size="sm" variant="outline-primary"></b-form-radio-group>
          </template>
        </dashboard-chart>
      </b-col>

      <!-- Performance & Idle Tracking Section -->
      <b-col lg="6">
        <vehicle-mini-list 
          title="Most Used Vehicles" 
          :items="topVehicles" 
          type="most-used" 
          @select-vehicle="selectVehicle"
          @open-master-details="onOpenVehicleMasterDetails"
          @view-all="scrollToDetails"
        />
      </b-col>

      <b-col lg="6">
        <vehicle-mini-list 
          title="Idle Vehicles (> 3 Days)" 
          :items="idleVehicles" 
          type="idle" 
          @select-vehicle="selectVehicle"
          @open-master-details="onOpenVehicleMasterDetails"
          @view-all="onIdleVehiclesDrilldown"
        />
      </b-col>
       <b-col lg="12">
        <b-card no-body shadow-sm>
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0 font-weight-bold text-primary">Fleet Status Details</h6>
            <small class="text-muted">Vehicle-wise status buckets</small>
          </b-card-header>
          <b-card-body>
            <b-tabs v-model="statusTabIndex" pills>
              <b-tab :title="`Available (${availableVehicles.length})`">
                <b-table
                  small
                  hover
                  striped
                  responsive
                  :items="availableVehicles"
                  :fields="statusVehicleFields"
                  class="mb-0"
                >
                  <template slot="index" slot-scope="row">
                    {{ row.index + 1 }}
                  </template>
                  <template slot="status" slot-scope="row">
                    <b-badge :variant="getStatusVariant(row.item)" pill>
                      {{ row.item.status || "N/A" }}
                    </b-badge>
                  </template>
                </b-table>
              </b-tab>
              <b-tab :title="`Booked (${bookedVehicles.length})`">
                <b-table
                  small
                  hover
                  striped
                  responsive
                  :items="bookedVehicles"
                  :fields="statusVehicleFields"
                  class="mb-0"
                >
                  <template slot="index" slot-scope="row">
                    {{ row.index + 1 }}
                  </template>
                  <template slot="status" slot-scope="row">
                    <b-badge :variant="getStatusVariant(row.item)" pill>
                      {{ row.item.status || "N/A" }}
                    </b-badge>
                  </template>
                </b-table>
              </b-tab>
              <b-tab :title="`Unknown (${unknownVehicles.length})`">
                <b-table
                  small
                  hover
                  striped
                  responsive
                  :items="unknownVehicles"
                  :fields="statusVehicleFields"
                  class="mb-0"
                >
                  <template slot="index" slot-scope="row">
                    {{ row.index + 1 }}
                  </template>
                  <template slot="status" slot-scope="row">
                    <b-badge :variant="getStatusVariant(row.item)" pill>
                      {{ row.item.status || "N/A" }}
                    </b-badge>
                  </template>
                </b-table>
              </b-tab>
              <b-tab :title="`Inactive (${inactiveVehicles.length})`">
                <b-table
                  small
                  hover
                  striped
                  responsive
                  :items="inactiveVehicles"
                  :fields="statusVehicleFields"
                  class="mb-0"
                >
                  <template slot="index" slot-scope="row">
                    {{ row.index + 1 }}
                  </template>
                  <template slot="status" slot-scope="row">
                    <b-badge :variant="getStatusVariant(row.item)" pill>
                      {{ row.item.status || "N/A" }}
                    </b-badge>
                  </template>
                </b-table>
              </b-tab>
            </b-tabs>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>


    <!-- Analytics Overview Row -->
    <b-row class="mb-4">
      <b-col lg="6">
        <dashboard-chart 
          title="Expense Breakdown" 
          type="donut" 
          height="320" 
          :options="expenseCategoryOptions" 
          :series="expenseCategorySeries"
          :loading="isExpenseCategoryLoading"
          footer="Categorized by transit and logistics ledger groups"
        />
      </b-col>
      <b-col lg="6">
        <dashboard-chart 
          title="Fleet Status" 
          type="donut" 
          height="320" 
          :options="fleetStatusDonutOptions" 
          :series="fleetStatusDonutSeries"
          :loading="isFleetStatusLoading"
          :footer="fleetStatusFooter"
        />
      </b-col>
    </b-row>

    <!-- Modals -->
    <vehicle-utilization-drilldown-modal
      :visible.sync="utilDrillModalVisible"
      :title="utilDrillModalTitle"
      :fields="utilDrillModalFields"
      :items="utilDrillModalItems"
      :loading="utilDrillModalLoading"
      :error-message="utilDrillModalError"
      :show-export="true"
      :is-exporting="isDrilldownExporting"
      :total="utilDrillModalTotal"
      :page="utilDrillModalPage"
      :page-size="utilDrillModalPageSize"
      @page-change="onDrilldownPageChange"
      @export="onDrilldownExport"
    />

    <vehicle-master-details-modal
      :visible.sync="vehicleMasterModalVisible"
      :vehicle-truck-id="masterVehicleId"
      :vehicle-no="masterVehicleNo"
    />


    <service-estimation-modal 
      :visible.sync="showServiceModal" 
      :service="selectedService" 
      :spare-parts="spareParts" 
      @download-estimate="downloadEstimate"
      @go-to-maintenance="goToMaintenanceModule"
    />
  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import * as XLSX from "xlsx";
import { ModelListSelect } from "vue-search-select";


// Component Components
import KpiCard from "./components/KpiCard.vue";
import DashboardChart from "./components/DashboardChart.vue";
import VehicleMiniList from "./components/VehicleMiniList.vue";
import UtilizationTab from "./components/UtilizationTab.vue";
import OperationsIntelligenceTab from "./components/OperationsIntelligenceTab.vue";
import LocationIntelligenceTab from "./components/LocationIntelligenceTab.vue";
import MaintenanceHealthTab from "./components/MaintenanceHealthTab.vue";
import VehiclePerformanceService from "../../../services/VehiclePerformanceService";
import FinancialAnalyticsTab from "./components/FinancialAnalyticsTab.vue";
import ServiceEstimationModal from "./components/ServiceEstimationModal.vue";
import VehicleUtilizationDrilldownModal from "./components/VehicleUtilizationDrilldownModal.vue";
import VehicleMasterDetailsModal from "./components/VehicleMasterDetailsModal.vue";


export default {
  name: "VehiclePerformanceDash",
  beforeMount() {
      this.$store.dispatch(
        "commonDropdownMaster/setAllVehicleDDList",
        JSON.stringify({
          itemCode: "getVehicleList",
          vehicleActiveInactiveId: "1",
        })
      );
      this.$store.dispatch("setCurrentRoute",this.$route.path);
      this.fetchVehicleMasterList();
      this.refreshData();
  },
  components: {
    ModelListSelect,
    DateRangePicker,
    KpiCard,
    DashboardChart,
    VehicleMiniList,
    UtilizationTab,
    OperationsIntelligenceTab,
    LocationIntelligenceTab,
    MaintenanceHealthTab,
    FinancialAnalyticsTab,
    ServiceEstimationModal,
    VehicleUtilizationDrilldownModal,
    VehicleMasterDetailsModal,
  },
  data() {
    return {
      summaryData: null,
      vehicleMasterList: [],
      selectedVehicleId: null,
      activeTab: 0,
      statusTabIndex: 0,
      chartPeriod: 'Monthly',
      viewMode: 'table',
      vehicleFilter: '',
      showServiceModal: false,
      selectedVehicleRegNo: null,
      utilDrillModalVisible: false,
      utilDrillModalTitle: "",
      utilDrillModalFields: [],
      utilDrillModalItems: [],
      utilDrillModalLoading: false,
      utilDrillModalError: "",
      isDrilldownExporting: false,
      isUtilizationExporting: false,
      utilizationTopN: 5,
      selectedService: null,
      utilDrillType: "trips",
      utilDrillModalTotal: 0,
      utilDrillModalPage: 1,
      utilDrillModalPageSize: 10,
      vehicleMasterModalVisible: false,
      masterVehicleId: null,
      masterVehicleNo: "",
      revenueBreakdownDimension: 'client',
      revenueBreakdownLoading: false,
      revenueFooter: "Analyzing fleet revenue...",
      
      // Financial Analytics Revenue State
      clientRevenueSeries: [],
      clientRevenueOptions: {},
      vehicleRevenueSeries: [],
      vehicleRevenueOptions: {},
      slRevenueSeries: [],
      slRevenueOptions: {},
      monthlyRevenueSeries: [],
      monthlyRevenueOptions: {},
      yearlyRevenueSeries: [],
      yearlyRevenueOptions: {},
      clientRevenueRaw: [],
      vehicleRevenueRaw: [],
      slRevenueRaw: [],
      monthlyRevenueRaw: [],
      yearlyRevenueRaw: [],

      // Financial Analytics Expense State
      clientExpenseSeries: [],
      clientExpenseOptions: {},
      vehicleExpenseSeries: [],
      vehicleExpenseOptions: {},
      slExpenseSeries: [],
      slExpenseOptions: {},
      monthlyExpenseSeries: [],
      monthlyExpenseOptions: {},
      yearlyExpenseSeries: [],
      yearlyExpenseOptions: {},
      clientExpenseRaw: [],
      vehicleExpenseRaw: [],
      slExpenseRaw: [],
      monthlyExpenseRaw: [],
      yearlyExpenseRaw: [],

      expenseCategorySeries: [],
      expenseCategoryOptions: {},
      fleetStatusDonutSeries: [],
      fleetStatusDonutOptions: {},
      fleetStatusFooter: "",
      isSummaryLoading: false,
      isTrendLoading: false,
      isExpenseCategoryLoading: false,
      isFleetStatusLoading: false,
      isFinanceLoading: false,
      isTripVolumeLoading: false,
      isAvailabilityLoading: false,
      isVehicleActivityLoading: false,
      isAccountingAuditLoading: false,
      isFuelEfficiencyLoading: false,
      
       // Operations Intelligence State
      tripVolumeStats: [],
      availabilityStats: { available: 0, booked: 0, inactive: 0, total: 0 },
      vehicleActivityStats: [],
      accountingAuditStats: { totalTrips: 0, billedTrips: 0, unbilledTrips: 0,billedRevenue:0 },
      fuelEfficiencyStats: [],

      isLocationTripLoading: false,
      isLocationDensityLoading: false,
      isMaintenanceLoading: false,

      // Maintenance State
      maintenanceHistory: [],
      maintenanceItemBrief: [],
      isItemBriefLoading: false,
      tripTrendsSeries: [],
      tripTrendsOptions: {
        chart: { 
          type: 'bar', 
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 800 }
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            horizontal: true,
            columnWidth: '60%',
            barHeight: '70%',
            distributed: true
          }
        },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6366f1', '#ec4899', '#f97316', '#14b8a6', '#06b6d4'],
        dataLabels: {
          enabled: true,
          textAnchor: 'start',
          style: { colors: ['#fff'], fontSize: '11px', fontWeight: 600 },
          formatter: function (val, opt) {
            return val + " trips";
          },
          offsetX: 0,
        },
        xaxis: {
          categories: [],
          labels: { style: { colors: '#64748b', fontSize: '11px' } }
        },
        yaxis: {
          labels: { style: { colors: '#1e293b', fontWeight: 600, fontSize: '12px' } }
        },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        tooltip: { theme: 'light', y: { formatter: (val) => val + " trips" } },
        legend: { show: false }
      },
      locationDensity: [],
      selectedLocationRole: 'PODLocationId',

      dateRange: {
        startDate: (function () {
          const now = moment();
          const startYear = now.month() >= 3 ? now.year() : now.year() - 1; // Apr-Mar FY
          return moment({ year: startYear, month: 3, day: 1 }).toDate();
        })(),
        endDate: (function () {
          const now = moment();
          const startYear = now.month() >= 3 ? now.year() : now.year() - 1; // Apr-Mar FY
          return moment({ year: startYear + 1, month: 2, day: 31 }).toDate();
        })()
      },
      locale: {
        // vue2-daterange-picker default dateUtil is "native" (not moment).
        // Native formatter expects lowercase tokens: d/m/y — NOT Moment-style DD/MM/YYYY.
        format: 'dd/mm/yyyy',
        separator: ' - '
      },
      kpiSummary: [
        { title: 'Total Active Vehicles', value: '0', icon: 'local_shipping', color: '#007bff', trend: 0 },
        { title: 'Total Trips', value: '0', icon: 'map', color: '#28a745', trend: 0 },
        { title: 'Total Revenue', value: '₹0', icon: 'account_balance_wallet', color: '#17a2b8', trend: 0 },
        { title: 'Total Expense', value: '₹0', icon: 'trending_down', color: '#dc3545', trend: 0 }
      ],
      chartOptions: {
        chart: { 
          type: 'area', 
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 800 }
        },
        colors: ['#3b82f6', '#ef4444'], // Modern Blue & Red
        xaxis: { 
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: { labels: { style: { colors: '#64748b' } } },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        stroke: { curve: 'smooth', width: 3 },
        fill: { 
          type: 'gradient', 
          gradient: { 
            shadeIntensity: 1, 
            opacityFrom: 0.45, 
            opacityTo: 0.05, 
            stops: [20, 100] 
          } 
        },
        dataLabels: { enabled: false },
        markers: { size: 0, hover: { size: 7 } },
        legend: { position: 'top', horizontalAlign: 'right', fontWeight: 500 }
      },
      chartSeries: [
        { name: 'Revenue', data: [310, 400, 280, 510, 420, 1090, 1000, 1200] },
        { name: 'Expense', data: [110, 320, 450, 320, 340, 520, 410, 600] }
      ],
      expenseDonutSeries: [58, 15, 12, 10, 5],
      expenseDonutOptions: {
        labels: ['Fuel', 'Tolls', 'Maintenance', 'Driver Wages', 'Others'],
        colors: ['#3b82f6', '#06b6d4', '#f43f5e', '#f59e0b', '#94a3b8'],
        legend: { position: 'bottom', fontSize: '12px' },
        plotOptions: { 
          pie: { 
            donut: { 
              size: '75%',
              labels: {
                show: true,
                total: { show: true, label: 'TOTAL', formatter: () => '₹2.8M', color: '#64748b' }
              }
            } 
          } 
        },
        stroke: { width: 0 },
        dataLabels: { enabled: false }
      },
      revenueDonutSeries: [1200, 850, 450, 300],
      revenueDonutOptions: {
        labels: ['Adidas', 'Samsung', 'Flipkart', 'Others'],
        colors: ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b'],
        legend: { position: 'bottom', fontSize: '12px' },
        plotOptions: { 
          pie: { 
            donut: { 
              size: '75%',
              labels: {
                show: true,
                total: { show: true, label: 'TOTAL', formatter: () => '₹2.8M', color: '#64748b' }
              }
            } 
          } 
        },
        stroke: { width: 0 },
        dataLabels: { enabled: false }
      },
      locationChartSeries: [{
        name: 'Visits/Trips',
        data: [142, 118, 95, 78, 62, 45]
      }],
      locationChartOptions: {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            borderRadius: 4,
            barHeight: '70%',
          }
        },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
        dataLabels: { enabled: true, formatter: (val) => val + " trips" },
        xaxis: { categories: ['Gurugram Hub', 'Mumbai CFS', 'Dadri ICD', 'Tughlakabad ICD', 'Loni ICD', 'Pipavav Port'] },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        legend: { show: false }
      },
      hubDensity: [
        { name: 'Gurugram Hub', region: 'Delhi NCR', vehicles: 24, turnaround: '4.2 hrs' },
        { name: 'Mumbai CFS', region: 'Maharashtra', vehicles: 18, turnaround: '8.5 hrs' },
        { name: 'Dadri ICD', region: 'Uttar Pradesh', vehicles: 12, turnaround: '6.1 hrs' },
        { name: 'Tughlakabad ICD', region: 'Delhi', vehicles: 9, turnaround: '5.2 hrs' },
        { name: 'Pipavav Port', region: 'Gujarat', vehicles: 7, turnaround: '12.4 hrs' }
      ],
      locationLogFields: [
        { key: 'regNo', label: 'Vehicle No', sortable: true },
        { key: 'lastLocation', label: 'Last Reported Location' },
        { key: 'timestamp', label: 'Time At Location' },
        { key: 'status', label: 'Current Status' },
        { key: 'assignment', label: 'Assigned Link' }
      ],
      locationVehicleLogs: [
        { regNo: 'UP16CT4521', lastLocation: 'Gurugram Hub', timestamp: '10 mins ago', status: 'In-Transit', assignment: 'ASG-55091' },
        { regNo: 'DL1LAA0982', lastLocation: 'Mumbai CFS', timestamp: '45 mins ago', status: 'Stopped', assignment: 'ASG-55102' },
        { regNo: 'HR55X1234', lastLocation: 'Dadri ICD', timestamp: '2 hrs ago', status: 'In-Transit', assignment: 'ASG-55115' },
        { regNo: 'UP14BT8893', lastLocation: 'Loni ICD', timestamp: '5 hrs ago', status: 'In-Transit', assignment: 'ASG-55120' }
      ],
      statusVehicleFields: [
        { key: "index", label: "SN." },
        { key: "vehicleTruckId", label: "ID", sortable: true },
        // { key: "vehicleNo", label: "Vehicle Owner/Name" },
        { key: "regdNo", label: "Vehicle No", sortable: true },
        { key: "engineNo", label: "Engine No" },
        { key: "status", label: "Status", sortable: true },
      ],
      fleetRadialSeries: [76, 24],
      fleetRadialOptions: {
        chart: { type: 'radialBar' },
        plotOptions: {
          radialBar: {
            dataLabels: {
              name: { fontSize: '18px' },
              value: { fontSize: '14px' },
              total: { show: true, label: 'Active', formatter: function (w) {
                return w.globals.seriesTotals[0] + "%"
              } }
            }
          }
        },
        labels: ['Active', 'Inactive'],
        colors: ['#10b981', '#ef4444'],
      },
      miniFleetOptions: {
        chart: { type: 'radialBar', sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            hollow: { size: '0%' }, // Remove hollow for tiny charts
            dataLabels: { show: false },
            track: { strokeWidth: '100%' }
          }
        },
        stroke: { lineCap: 'butt' },
        colors: ['#10b981']
      },
      revenueDimensionLabels: {
        client: 'By Client',
        vehicle: 'By Vehicle',
        shippingLine: 'By Shipping Line',
        month: 'By Month',
        year: 'By Year'
      },
      tripVolumeSeries: [{
        name: 'Total Trips',
        data: [142, 175, 150, 210, 195, 250, 230, 285]
      }],
      tripVolumeOptions: {
        chart: { toolbar: { show: false } },
        colors: ['#8b5cf6'],
        stroke: { curve: 'stepline', width: 3 },
        xaxis: { categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'] },
        fill: { 
          type: 'gradient', 
          gradient: { opacityFrom: 0.5, opacityTo: 0.1 } 
        }
      },
      availabilitySeries: [65, 35],
      availabilityOptions: {
        labels: ['Booked', 'Free'],
        colors: ['#3b82f6', '#10b981'],
        plotOptions: {
          radialBar: {
            dataLabels: {
              total: { show: true, label: 'Available', formatter: () => '35%' }
            }
          }
        }
      },
      vehicleTripsSeries: [{
        name: 'Trips',
        data: [42, 38, 35, 28, 22]
      }],
      vehicleTripsOptions: {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: { borderRadius: 4, horizontal: false, columnWidth: '45%', distributed: true }
        },
        colors: ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e'],
        xaxis: {
          categories: ['UP16CT4521', 'DL1LAA0982', 'HR55X1234', 'UP14BT8893', 'MH01AB5566'],
          labels: { rotate: -45, style: { fontSize: '10px' } }
        },
        legend: { show: true, position: 'top', horizontalAlign: 'right' },
        dataLabels: { 
          enabled: true,
          enabledOnSeries: [0]
        },
        yaxis: [
          {
            title: { text: 'Trips' },
          },
          {
            opposite: true,
            title: { text: 'Fuel Expense (₹)' }
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (y, { seriesIndex }) {
              if (seriesIndex === 1) return "₹" + Number(y).toLocaleString('en-IN');
              return y + " trips";
            }
          }
        }
      },
      fuelTrendSeries: [{
        name: 'Efficiency',
        data: [4.2, 4.5, 4.1, 4.8, 4.6, 5.0, 4.9, 5.2]
      }],
      fuelTrendOptions: {
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { width: 5, curve: 'smooth' },
        xaxis: { 
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
          axisBorder: { show: false }
        },
        yaxis: { labels: { formatter: (val) => val.toFixed(1) } },
        colors: ['#10b981'],
        fill: {
          type: 'gradient',
          gradient: { shade: 'dark', gradientToColors: ['#34d399'], shadeIntensity: 1, type: 'horizontal', opacityFrom: 1, opacityTo: 1, stops: [0, 100, 100, 100] }
        },
        markers: { size: 0 },
        grid: { borderColor: '#f1f5f9' }
      },
      baseUtilizationChartOptions: {
        chart: { 
          type: 'radialBar',
          animations: { enabled: true, speed: 1000, animateGradually: { enabled: true, delay: 150 } }
        },
        plotOptions: {
          radialBar: {
            offsetY: 0,
            startAngle: 0,
            endAngle: 270,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent',
            },
            track: {
              show: true,
              background: '#f1f5f9',
              strokeWidth: '100%',
              opacity: 1,
              margin: 5, 
            },
            dataLabels: {
              name: { show: false },
              value: { show: false },
            }
          }
        },
        colors: ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e'],
        labels: [],
        legend: {
          show: true,
          floating: true,
          fontSize: '13px',
          position: 'left',
          offsetX: 10,
          offsetY: 10,
          labels: { useSeriesColors: true },
          markers: { size: 0 },
          formatter: function(seriesName, opts) {
            return seriesName + ":  " + opts.w.globals.series[opts.seriesIndex] + "%"
          },
          itemMargin: { vertical: 3 }
        },
        tooltip: {
          enabled: true,
          theme: 'dark',
          y: {
            formatter: (val) => val + '%'
          }
        },
        stroke: { lineCap: 'round' }
      },
      topVehicles: [],
      idleVehicles: [],
      utilizationFields: [
        { key: 'regNo', label: 'Vehicle No', sortable: true },
        { key: 'vehicle_status', label: 'Vehicle Status', sortable: true },
        { key: 'driver', label: 'Primary Driver', sortable: true },
        { key: 'lastLocation', label: 'Current Location' },
        { key: 'trips', label: 'Trips', sortable: true },
        { key: 'totalAssignments', label: 'Assignments', sortable: true },
        { key: 'revenue', label: 'Total Tpt Sale', sortable: true },
        { key: 'expense', label: 'Total Tpt Expense', sortable: true },
        { key: 'performance', label: 'Utilization %', sortable: true },
        { key: 'pendingFineImposed', label: 'Pending Fine Imposed', sortable: true },
        { key: 'disposedFineImposed', label: 'Disposed Fine Imposed', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      vehicleUtilization: [],
      maintenanceFields: [
        { key: 'id', label: 'Ticket ID' },
        { key: 'regNo', label: 'Vehicle No' },
        { key: 'date', label: 'Service Date', sortable: true },
        { key: 'type', label: 'Service Type' },
        { key: 'status', label: 'Status' },
        { key: 'cost', label: 'Est. Cost' },
        { key: 'actions', label: 'Action' }
      ],
      maintenanceRecords: [
        { id: 'MT-4401', regNo: 'UP16CT4521', date: '2024-03-01', type: 'Full Service', status: 'Completed', cost: '₹12,400', maintId: 'MID-881', gateId: 'GID-1002' },
        { id: 'MT-4405', regNo: 'DL1LAA0982', date: '2024-03-05', type: 'Engine Check', status: 'Completed', cost: '₹5,800', maintId: 'MID-885', gateId: 'GID-1009' },
        { id: 'MT-4412', regNo: 'UP16CT4521', date: '2024-03-12', type: 'Brake Realignment', status: 'Pending', cost: '₹8,200', maintId: 'MID-892', gateId: 'GID-1015' }
      ],
      spareParts: [
        { name: 'Engine Oil (Shell Helix Ultra)', qty: '5 Liters', price: '4,500' },
        { name: 'Brake Pad Set (Front)', qty: '1 Set', price: '3,200' },
        { name: 'Air Filter (OEM)', qty: '1 Unit', price: '1,200' },
        { name: 'Labor & Service Charges', qty: '-', price: '3,500' }
      ]
    };
  },
  computed: {
    dimensionLabel() {
      return this.revenueDimensionLabels[this.revenueBreakdownDimension];
    },
    availableVehicles() {
      return this.summaryData && Array.isArray(this.summaryData.available)
        ? this.summaryData.available
        : [];
    },
    bookedVehicles() {
      return this.summaryData && Array.isArray(this.summaryData.booked)
        ? this.summaryData.booked
        : [];
    },
    unknownVehicles() {
      return this.summaryData && Array.isArray(this.summaryData.unknown)
        ? this.summaryData.unknown
        : [];
    },
    inactiveVehicles() {
      return this.summaryData && Array.isArray(this.summaryData.inactive)
        ? this.summaryData.inactive
        : [];
    },
    filteredKpiSummary() {
      if (!this.selectedVehicleRegNo) return this.kpiSummary;
      // Mock vehicle-specific KPI data
      return [
        { title: 'Vehicle Status', value: 'Active', icon: 'local_shipping', color: '#007bff', trend: 0 },
        { title: 'Trips This Month', value: '14', icon: 'map', color: '#28a745', trend: 8.5 },
        { title: 'Avg Revenue / Trip', value: '₹17,500', icon: 'account_balance_wallet', color: '#17a2b8', trend: 1.2 },
        { title: 'Next Service In', value: '1,200 km', icon: 'build', color: '#dc3545', trend: 0 }
      ];
    },
    utilizationChartSeries() {
      return this.topUtilizationVehicles.map(v => v.performance);
    },
    utilizationChartOptions() {
      const topData = this.topUtilizationVehicles;
      return Object.assign({}, this.baseUtilizationChartOptions, {
        labels: topData.map(v => v.regNo),
        legend: Object.assign({}, this.baseUtilizationChartOptions.legend, {
          show: topData.length <= 20 // Hide floating legend if too many items
        })
      });
    },
    topUtilizationVehicles() {
      let list = this.vehicleUtilization;
      if (this.vehicleFilter) {
        const f = this.vehicleFilter.toLowerCase();
        list = list.filter(v => 
          (v.regNo && v.regNo.toLowerCase().includes(f)) ||
          (v.driver && v.driver.toLowerCase().includes(f)) ||
          (v.lastLocation && v.lastLocation.toLowerCase().includes(f))
        );
      }
      if (this.selectedVehicleRegNo) {
        list = list.filter(v => v.regNo === this.selectedVehicleRegNo);
      }
      const sorted = list.slice().sort((a, b) => b.performance - a.performance);
      if (this.utilizationTopN === 'all') {
        return sorted;
      }
      return sorted.slice(0, Number(this.utilizationTopN));
    },
    filteredChartSeries() {
      if (!this.selectedVehicleRegNo) return this.chartSeries;
      // Mock vehicle-specific chart data
      return [
        { name: 'Revenue', data: [12, 15, 12, 18, 14, 25, 22, 28] },
        { name: 'Expense', data: [8, 10, 9, 12, 10, 15, 14, 18] }
      ];
    },
    filteredVehicleUtilization() {
      if (!this.selectedVehicleRegNo) return this.vehicleUtilization;
      return this.vehicleUtilization.filter(item => item.regNo === this.selectedVehicleRegNo);
    },
    filteredMaintenanceRecords() {
      if (!this.selectedVehicleRegNo) return this.maintenanceRecords;
      return this.maintenanceRecords.filter(item => item.regNo === this.selectedVehicleRegNo);
    },
    filteredLocationVehicleLogs() {
      if (!this.selectedVehicleRegNo) return this.locationVehicleLogs;
      return this.locationVehicleLogs.filter(item => item.regNo === this.selectedVehicleRegNo);
    },
    tripDrillFields() {
      const fmt = (v) => this.formatDrillDate(v);
      const money = (v) =>
        v == null || v === "" ? "—" : "₹" + Number(v).toLocaleString("en-IN");
      return [
        { key: "index", label: "SN." },
        { key: "goodReceivedId", label: "GR ID" },

        { key: "jobNo", label: "Job No." },
        { key: "jobDate", label: "Job Date", formatter: (v) => fmt(v) },
        { key: "jobCreatedBy", label: "Job Raised By" },
        { key: "jobType", label: "Job Type" },
        { key: "branch", label: "Branch" },
        { key: "sline", label: "SLine" },
        { key: "client", label: "Client" },
        { key: "bookingNo", label: "Booking No." },
        { key: "enquiryNo", label: "Enq No." },
        { key: "enquiryType", label: "Enq type" },
        { key: "shipperInvNo", label: "Shipper INV no." },
        { key: "por", label: "POR" },
        { key: "pol", label: "POL" },
        { key: "pod", label: "POD" },
        { key: "fpod", label: "FPOD" },
        { key: "stuffingLocation", label: "Stuff" },
        { key: "handoverLocation", label: "Hand" },
        { key: "pickupLocation", label: "Pickup" },
        { key: "assignmentSeries", label: "Assignment" },
        { key: "grnNo", label: "GRN No." },
        { key: "grnDate", label: "GRN Date", formatter: (v) => fmt(v) },
        { key: "grnCreatedBy", label: "GRN Raised By" },
        { key: "vehicleNo", label: "Vehicle No." },
        { key: "fuelSlipNo", label: "Fuel Slip No." },
        { key: "containerNo", label: "Container No." },
        { key: "shippingLineName", label: "Shipping Line" },
        { key: "shippingLineSealNo", label: "Line Seal No." },
        { key: "transportationBy", label: "Transportation By" },
        { key: "transporterName", label: "Transporter" },
        { key: "vehicleStatusGrn", label: "Status (GRN)" },
        { key: "vehicleMasterStatus", label: "Vehicle status" },
        { key: "movementType", label: "Movement type" },
        { key: "skipGrn", label: "Skip GRN" },
        { key: "refuelingStationName", label: "Re-fuel station" },
        { key: "refuelingStationAddress", label: "Re-fuel address" },
        { key: "advanceCash", label: "Advance cash" },
        { key: "actualCashDispensed", label: "Actual cash dispensed" },
        { key: "diesel", label: "Diesel" },
        { key: "actualDieselDispensed", label: "Actual diesel dispensed" },
        { key: "tollInCash", label: "Toll (cash)" },
        { key: "amountPaidCashForDiesel", label: "Cash for diesel" },
        { key: "dieselPrice", label: "Diesel price" },
        { key: "containerSizeType", label: "Container size-type" },
        { key: "vehicleType", label: "Vehicle type" },
        { key: "consignee", label: "Consignee" },
        { key: "consignor", label: "Consignor" },
        { key: "customSealNo", label: "Custom seal" },
        {
          key: "salesAmountAssignmentWise",
          label: "Income (assignment)",
          formatter: (v) => money(v),
        },
        {
          key: "expenseAssignmentWise",
          label: "Expense (assignment)",
          formatter: (v) => money(v),
        },
        {
          key: "pendingFineImposed",
          label: "Pending Fine",
          formatter: (v) => money(v),
        },
        {
          key: "disposedFineImposed",
          label: "Disposed Fine",
          formatter: (v) => money(v),
        },
        { key: "allModuleId", label: "Module ID" },
      ];
    },
    assignmentDrillFields() {
      const fmt = (v) => this.formatDrillDate(v);
      return [
        { key: "index", label: "SN." },
        { key: "allModuleId", label: "Job ID" },
        { key: "jobNo", label: "Job No." },
        { key: "jobType", label: "Job Type" },
        { key: "jobDate", label: "Job Date", formatter: (v) => fmt(v) },
        { key: "branch", label: "Branch" },
        { key: "sline", label: "SLine" },
        { key: "client", label: "Client" },
        { key: "bookingNo", label: "Booking no." },
        { key: "enquiryNo", label: "Enq no." },
        { key: "enquiryType", label: "Enq type" },
        { key: "por", label: "POR" },
        { key: "pol", label: "POL" },
        { key: "pod", label: "POD" },
        { key: "fpod", label: "FPOD" },
        { key: "stuffingLocation", label: "Stuff" },
        { key: "handoverLocation", label: "Hand" },
        { key: "pickupLocation", label: "Pickup" },
        { key: "salesAmountAssignmentWise", label: "Income" },
        { key: "expenseAssignmentWise", label: "Expense" },
      ];
    },
    idleDrillFields() {
      const fmt = (v) => this.formatDrillDate(v);
      return [
        { key: "index", label: "SN." },
        { key: "regNo", label: "Vehicle No." },
        { key: "grnNo", label: "Last GRN No." },
        { key: "assignmentNo", label: "Last Assignment" },
        { key: "status", label: "Status" },
        { key: "lastActive", label: "Last Active", formatter: (v) => fmt(v) },
        { key: "idleDays", label: "Idle Days", sortable: true },
        { key: "client", label: "Client" },
        { key: "sline", label: "Sline" },
        { key: "por", label: "POR" },
        { key: "pol", label: "POL" },
        { key: "pod", label: "POD" },
        { key: "fod", label: "FPOD" },
        { key: "pick", label: "Pick" },
        { key: "stuff", label: "Stuff" },
        { key: "hand", label: "Hand" },
      ];
    },
  },
  watch: {
  },
  methods: {
    async fetchSummaryData() {
      this.isSummaryLoading = true;
      this.isFleetStatusLoading = true;
      try {
        const res = await VehiclePerformanceService.getSummaryData(
          this.utilizationDateParams(),
        );
        if (res.data && res.data.status) {
          this.summaryData = res.data.data;
          this.updateKpiSummary(res.data.data);
          this.updateFleetStatusDonut(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
      } finally {
        this.isSummaryLoading = false;
        this.isFleetStatusLoading = false;
      }
    },
    async fetchVehicleMasterList() {
      try {
        const res = await VehiclePerformanceService.getVehicleMasterList();
        if (res.data && res.data.status) {
          this.vehicleMasterList = res.data.data;
        }
      } catch (err) {
        console.error("Error fetching vehicle master list:", err);
      }
    },
    async fetchExpenseCategoryBreakdown() {
      this.isExpenseCategoryLoading = true;
      try {
        const params = this.utilizationDateParams();
        const res =
          await VehiclePerformanceService.getExpenseCategoryBreakdown(params);
        if (res.data && res.data.status) {
          this.processExpenseCategoryData(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching expense category breakdown:", err);
      } finally {
        this.isExpenseCategoryLoading = false;
      }
    },
    processExpenseCategoryData(data) {
      if (!data || data.length === 0) {
        this.expenseCategorySeries = [];
        return;
      }
      this.expenseCategorySeries = data.map((d) => Number(d.value));
      this.expenseCategoryOptions = {
        labels: data.map((d) => d.label),
        colors: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"],
        dataLabels: { enabled: false },
        legend: { position: "bottom" },
        stroke: { show: false },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "TOTAL",
                  formatter: () =>
                    "₹" +
                    (
                      this.expenseCategorySeries.reduce((a, b) => a + b, 0) /
                      1000000
                    ).toFixed(1) +
                    "M",
                },
              },
            },
          },
        },
      };
    },
    updateFleetStatusDonut(data) {
      const active = data.activeVehicles || 0;
      const inactive = data.inactiveVehicles || 0;
      this.fleetStatusDonutSeries = [active, inactive];
      this.fleetStatusDonutOptions = {
        labels: ["Active", "Inactive"],
        colors: ["#10b981", "#ef4444"],
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: { show: false },
        plotOptions: {
          pie: {
            donut: {
              size: "75%",
              labels: {
                show: true,
                name: { show: true, fontSize: "14px", color: "#64748b" },
                value: { show: true, fontSize: "22px", fontWeight: "bold" },
                total: {
                  show: true,
                  label: "Active",
                  formatter: () =>
                    active + inactive > 0
                      ? Math.round((active / (active + inactive)) * 100) + "%"
                      : "0%",
                },
              },
            },
          },
        },
      };
      this.fleetStatusFooter = `${active} Vehicles Active | ${inactive} Inactive`;
    },
    updateKpiSummary(data) {
      if (!this.kpiSummary) return;
      if (this.kpiSummary[0])
        this.kpiSummary[0].value = String(data.activeVehicles || 0);
      if (this.kpiSummary[1])
        this.kpiSummary[1].value = String(data.totalTrips || 0);
      if (this.kpiSummary[2])
        this.kpiSummary[2].value =
          "₹" + Number(data.totalRevenue || 0).toLocaleString("en-IN");
      if (this.kpiSummary[3])
        this.kpiSummary[3].value =
          "₹" + Number(data.totalExpense || 0).toLocaleString("en-IN");
    },
    async fetchAllRevenueBreakdowns() {
      this.isFinanceLoading = true;
      const dimensions = ["client", "vehicle", "shippingLine", "month", "year"];
      const params = this.utilizationDateParams();

      const requests = dimensions.map((dim) =>
        VehiclePerformanceService.getRevenueBreakdown(dim, params).catch((e) => {
          console.error(`Error fetching ${dim} revenue:`, e);
          return { data: { status: false } };
        }),
      );

      const results = await Promise.all(requests);

      results.forEach((res, index) => {
        if (res.data && res.data.status) {
          const dim = dimensions[index];
          const data = res.data.data || [];
          this.processFinancialData("revenue", dim, data);
        }
      });
      this.isFinanceLoading = false;
    },
    async fetchAllExpenseBreakdowns() {
      this.isFinanceLoading = true;
      const dimensions = ["client", "vehicle", "shippingLine", "month", "year"];
      const params = this.utilizationDateParams();

      const requests = dimensions.map((dim) =>
        VehiclePerformanceService.getExpenseBreakdown(dim, params).catch((e) => {
          console.error(`Error fetching ${dim} expense:`, e);
          return { data: { status: false } };
        }),
      );

      const results = await Promise.all(requests);

      results.forEach((res, index) => {
        if (res.data && res.data.status) {
          const dim = dimensions[index];
          const data = res.data.data || [];
          this.processFinancialData("expense", dim, data);
        }
      });
      this.isFinanceLoading = false;
    },
    async fetchTrendData() {
      this.isTrendLoading = true;
      try {
        const res = await VehiclePerformanceService.getRevenueVsExpenseTrend(this.utilizationDateParams());
        if (res.data && res.data.status) {
          const data = res.data.data || [];
          
          // Labels (Months)
          const labels = data.map(item => item.label);
          
          // Series Data
          const revenueData = data.map(item => item.revenue);
          const expenseData = data.map(item => item.expense);

          // Update Chart
          const newOptions = Object.assign({}, this.chartOptions);
          newOptions.xaxis = Object.assign({}, newOptions.xaxis, {
            categories: labels
          });
          this.chartOptions = newOptions;
          
          this.chartSeries = [
            { name: 'Revenue', data: revenueData },
            { name: 'Expense', data: expenseData }
          ];
        }
      } catch (e) {
        console.error("fetchTrendData error:", e);
      }
    },
    async fetchOperationsIntelligence() {
      const params = this.utilizationDateParams();
      
      this.isTripVolumeLoading = true;
      this.isAvailabilityLoading = true;
      this.isVehicleActivityLoading = true;
      this.isAccountingAuditLoading = true;
      this.isFuelEfficiencyLoading = true;

      try {
        await Promise.all([
          VehiclePerformanceService.getTripVolumeTrends(params).then(res => {
            if (res.data && res.data.status) {
              this.tripVolumeStats = res.data.data || [];
              this.updateTripVolumeChart(this.tripVolumeStats);
            }
          }).finally(() => this.isTripVolumeLoading = false),

          VehiclePerformanceService.getAvailabilityData(params).then(res => {
            if (res.data && res.data.status) {
              this.availabilityStats = res.data.data;
              this.updateAvailabilityChart(this.availabilityStats);
            }
          }).finally(() => this.isAvailabilityLoading = false),

          VehiclePerformanceService.getVehicleTripActivity(params).then(res => {
            if (res.data && res.data.status) {
              this.vehicleActivityStats = res.data.data || [];
              this.updateVehicleActivityChart(this.vehicleActivityStats);
            }
          }).finally(() => this.isVehicleActivityLoading = false),

          VehiclePerformanceService.getTransportAccountingAudit(params).then(res => {
            if (res.data && res.data.status) {
              this.accountingAuditStats = res.data.data;
            }
          }).finally(() => this.isAccountingAuditLoading = false),

          VehiclePerformanceService.getFuelEfficiencyTrend(params).then(res => {
            if (res.data && res.data.status) {
              this.fuelEfficiencyStats = res.data.data || [];
              this.updateFuelEfficiencyChart(this.fuelEfficiencyStats);
            }
          }).finally(() => this.isFuelEfficiencyLoading = false)
        ]);
      } catch (e) {
        console.error("fetchOperationsIntelligence error:", e);
      }
    },
    updateTripVolumeChart(data) {
      if (!data) return;
      this.tripVolumeSeries = [{
        name: 'Total Trips',
        data: data.map(d => d.trips)
      }];
      this.tripVolumeOptions = Object.assign({}, this.tripVolumeOptions, {
        xaxis: { categories: data.map(d => moment(d.month, 'YYYY-MM').format('MMM YY')) }
      });
    },
    updateAvailabilityChart(data) {
      if (!data) return;
      this.availabilitySeries = [data.booked, data.available];
      const availablePercent = data.total > 0 ? Math.round((data.available / data.total) * 100) : 0;
      this.availabilityOptions = Object.assign({}, this.availabilityOptions, {
        plotOptions: {
          radialBar: {
            dataLabels: {
              total: { show: true, label: 'Available', formatter: () => availablePercent + '%' }
            }
          }
        }
      });
    },
    updateVehicleActivityChart(data) {
      if (!data) return;
      const topData = data.slice(0, 10);
      this.vehicleTripsSeries = [
        {
          name: 'Trips',
          type: 'column',
          data: topData.map(d => d.tripCount)
        },
        {
          name: 'Fuel Expense',
          type: 'line',
          data: topData.map(d => Number(d.totalFuelExpense || 0))
        }
      ];
      this.vehicleTripsOptions = Object.assign({}, this.vehicleTripsOptions, {
        xaxis: { 
          categories: topData.map(d => d.regdNo),
          labels: { rotate: -45, style: { fontSize: '10px' } }
        },
        plotOptions: {
          bar: { distributed: false } // Disable distributed when having multiple series
        },
        colors: ['#3b82f6', '#ef4444'], // Blue for trips, red for fuel
      });
    },
    updateFuelEfficiencyChart(data) {
      if (!data) return;
      this.fuelTrendSeries = [{
        name: 'Efficiency',
        data: data.map(d => Number(Number(d.avgKmpl || 0).toFixed(2)))
      }];
      this.fuelTrendOptions = Object.assign({}, this.fuelTrendOptions, {
        xaxis: { 
          categories: data.map(d => moment(d.month, 'YYYY-MM').format('MMM YY')),
          axisBorder: { show: false }
        }
      });
    },
    processFinancialData(type, dim, data) {
      if (!data || data.length === 0) return;

      const sorted = data.slice().sort((a, b) => b.value - a.value);
      const topN = (dim === 'month' || dim === 'year') ? 12 : 5;
      const displayData = sorted.slice(0, topN);
      
      if (dim !== 'month' && dim !== 'year' && sorted.length > topN) {
        const othersValue = sorted.slice(topN).reduce((sum, item) => sum + item.value, 0);
        if (othersValue > 0) {
          displayData.push({ label: 'Others', value: othersValue });
        }
      }

      const series = displayData.map(d => d.value);
      const labels = displayData.map(d => d.label);
      const colors = type === 'revenue' 
        ? ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#f43f5e', '#06b6d4', '#94a3b8']
        : ['#ef4444', '#f87171', '#fb923c', '#fbbf24', '#a1a1aa', '#71717a'];

      const baseOptions = {
        labels,
        colors,
        dataLabels: { enabled: false },
        legend: { position: 'bottom', fontSize: '11px' },
        tooltip: {
          y: { formatter: (val) => "₹" + Number(val).toLocaleString("en-IN") }
        }
      };

      const suffix = type.charAt(0).toUpperCase() + type.slice(1); // 'Revenue' or 'Expense'
      let normalizedDim = dim;
      if (dim === 'shippingLine') normalizedDim = 'sl';
      if (dim === 'month') normalizedDim = 'monthly';
      if (dim === 'year') normalizedDim = 'yearly';

      const seriesKey = `${normalizedDim}${suffix}Series`;
      const optionsKey = `${normalizedDim}${suffix}Options`;
      const rawKey = `${normalizedDim}${suffix}Raw`;

      if (dim === 'client' || dim === 'vehicle' || dim === 'shippingLine') {
        this[seriesKey] = series;
        this[optionsKey] = Object.assign({}, baseOptions, {
          plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'TOTAL', formatter: () => '₹' + (series.reduce((a,b)=>a+b,0)/1000000).toFixed(1) + 'M' } } } } }
        });
      } else if (dim === 'month') {
        this[seriesKey] = [{ name: suffix, data: series }];
        this[optionsKey] = {
          chart: { type: 'area', toolbar: { show: false } },
          xaxis: { categories: labels },
          colors: [colors[0]],
          stroke: { curve: 'smooth', width: 3 },
          fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.1 } },
          dataLabels: { enabled: false }
        };
      } else if (dim === 'year') {
        this[seriesKey] = [{ name: suffix, data: series }];
        this[optionsKey] = {
          chart: { type: 'bar', toolbar: { show: false } },
          xaxis: { categories: labels },
          colors: [colors[0]],
          plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
          dataLabels: { enabled: true, formatter: (v) => '₹' + (v/1000000).toFixed(1) + 'M' }
        };
      }

      this[rawKey] = sorted;
    },
    async fetchRevenueBreakdown() {
      this.revenueBreakdownLoading = true;
      try {
        const res = await VehiclePerformanceService.getRevenueBreakdown(
          this.revenueBreakdownDimension,
          this.utilizationDateParams()
        );
        if (res.data && res.data.status) {
          const data = res.data.data || [];
          
          if (data.length > 0) {
            // Sort and take top 5, group rest as Others
            const sorted = data.slice().sort((a, b) => b.value - a.value);
            const top5 = sorted.slice(0, 5);
            const othersValue = sorted.slice(5).reduce((sum, item) => sum + item.value, 0);
            
            if (othersValue > 0) {
              top5.push({ label: 'Others', value: othersValue });
            }

            this.revenueDonutSeries = top5.map(d => d.value);
            this.revenueDonutOptions = Object.assign({}, this.revenueDonutOptions, {
              labels: top5.map(d => d.label)
            });

            const total = this.revenueDonutSeries.reduce((a, b) => a + b, 0);
            const top3Sum = this.revenueDonutSeries.slice(0, 3).reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((top3Sum / total) * 100) : 0;
            
            this.revenueFooter = `Top 3 ${this.revenueBreakdownDimension}s contribute ${percentage}% of total revenue`;
            
            // Update center total label
            this.revenueDonutOptions.plotOptions.pie.donut.labels.total.formatter = () => 
              '₹' + (total / 1000000).toFixed(1) + 'M';
          }
        }
      } catch (e) {
        console.error("fetchRevenueBreakdown error:", e);
      } finally {
        this.revenueBreakdownLoading = false;
      }
    },
    setRevenueDimension(dim) {
      this.revenueBreakdownDimension = dim;
      this.fetchRevenueBreakdown();
    },
    /** Query params for APIs: use the dates actually selected in the picker (local calendar day). */
    utilizationDateParams() {
      const start = this.dateRange && this.dateRange.startDate;
      const end = this.dateRange && this.dateRange.endDate;
      return {
        startDate: moment(start || undefined).format("YYYY-MM-DD"),
        endDate: moment(end || undefined).format("YYYY-MM-DD"),
        vehicleTruckId: this.selectedVehicleId
      };
    },
    onDateRangeSelected(range) {
      if (!range || !range.startDate || !range.endDate) return;
      this.dateRange = {
        startDate: new Date(range.startDate),
        endDate: new Date(range.endDate),
      };
      this.refreshData();
    },
    computeUtilizationPercent(trips) {
      const t = Number(trips) || 0;
      let p = t > 0 ? Math.round((t / 30) * 100) : 0;
      return Math.min(100, p);
    },
    formatDrillDate(v) {
      if (v === null || v === undefined || v === "") return "—";
      const m = moment(v);
      return m.isValid() ? m.format("DD MMM YYYY HH:mm") : String(v);
    },
    async fetchVehicleUtilization() {
      try {
        const response = await VehiclePerformanceService.getUtilizationData(this.utilizationDateParams());
        if (response.data && response.data.status) {
          const apiData = response.data.data;
          if (Array.isArray(apiData)) {
            this.vehicleUtilization = apiData.map((row) =>
              Object.assign({}, row, {
                performance: this.computeUtilizationPercent(row.trips),
                totalAssignments:
                  row.totalAssignments != null ? Number(row.totalAssignments) : 0,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error fetching vehicle utilization data:", error);
      }
    },
    async onUtilizationTripDrilldown(vehicle) {
      const vehicleTruckId = vehicle ? vehicle.vehicleTruckId : null;
      this.selectedVehicleRegNo = vehicle ? (vehicle.regNo || vehicle.regdNo) : 'all';
      this.utilDrillType = "trips";
      this.selectedVehicleForDrilldown = vehicleTruckId;
      this.utilDrillModalTitle = `Trip Details — ${this.selectedVehicleRegNo === 'all' ? 'All Fleet' : this.selectedVehicleRegNo}`;
      this.utilDrillModalFields = this.tripDrillFields;
      this.utilDrillModalPage = 1;
      this.utilDrillModalTotal = 0;
      this.utilDrillModalVisible = true;
      await this.loadDrilldownPage();
    },
    async onUtilizationAssignmentDrilldown(vehicle) {
      const vehicleTruckId = vehicle ? vehicle.vehicleTruckId : null;
      this.selectedVehicleRegNo = vehicle ? (vehicle.regNo || vehicle.regdNo) : 'all';
      this.utilDrillType = "assignments";
      this.selectedVehicleForDrilldown = vehicleTruckId;
      this.utilDrillModalTitle = `Assignments — ${this.selectedVehicleRegNo === 'all' ? 'All Fleet' : this.selectedVehicleRegNo}`;
      this.utilDrillModalFields = this.assignmentDrillFields;
      this.utilDrillModalPage = 1;
      this.utilDrillModalTotal = 0;
      this.utilDrillModalVisible = true;
      await this.loadDrilldownPage();
    },
    async onIdleVehiclesDrilldown() {
      this.utilDrillType = "idle";
      this.utilDrillModalTitle = "All Idle Vehicles (> 3 Days)";
      this.utilDrillModalFields = this.idleDrillFields;
      this.utilDrillModalPage = 1;
      this.utilDrillModalTotal = 0;
      this.utilDrillModalVisible = true;
      await this.loadDrilldownPage();
    },
    async onDrilldownPageChange(page) {
      this.utilDrillModalPage = page;
      await this.loadDrilldownPage();
    },
    async loadDrilldownPage() {
      this.utilDrillModalItems = [];
      this.utilDrillModalLoading = true;
      this.utilDrillModalError = "";
      try {
        let res;
        const params = {
          page: this.utilDrillModalPage,
          pageSize: this.utilDrillModalPageSize,
        };

        if (this.utilDrillType === "idle") {
          res = await VehiclePerformanceService.getIdleVehicles(params);
        } else {
          Object.assign(params, this.utilizationDateParams(), { vehicleTruckId: this.selectedVehicleForDrilldown });
          if (this.utilDrillType === "trips") {
            res = await VehiclePerformanceService.getUtilizationTripDetails(params);
          } else {
            res = await VehiclePerformanceService.getUtilizationAssignmentDetails(params);
          }
        }

        if (res.data && res.data.status) {
          const apiData = res.data.data;
          if (this.utilDrillType === "idle") {
            this.utilDrillModalItems = apiData.items || [];
            this.utilDrillModalTotal = apiData.total || 0;
          } else {
            this.utilDrillModalItems = apiData || [];
            this.utilDrillModalTotal = res.data.total || (apiData || []).length;
          }
        } else {
          this.utilDrillModalError = (res.data && res.data.message) || "Unable to load details.";
        }
      } catch (e) {
        this.utilDrillModalError = (e.response && e.response.data && e.response.data.message) || e.message || "Request failed.";
      } finally {
        this.utilDrillModalLoading = false;
      }
    },
    onOpenVehicleMasterDetails(vehicle) {
      if (!vehicle) return;
      this.masterVehicleId = vehicle.vehicleTruckId;
      this.masterVehicleNo = vehicle.regNo || vehicle.regdNo || vehicle.vehicleNo;
      this.vehicleMasterModalVisible = true;
    },
    async onDrilldownExport() {
      if (this.utilDrillType === "idle") {
        await this.exportIdleVehiclesToExcel();
      } else {
        await this.exportDrilldownToExcel();
      }
    },
    async exportIdleVehiclesToExcel() {
      this.isDrilldownExporting = true;
      try {
        const res = await VehiclePerformanceService.getIdleVehicles({ pageSize: -1 });
        if (res.data && res.data.status) {
          const items = res.data.data || [];
          const excelData = items.map((item, idx) => ({
            "SN.": idx + 1,
            "Vehicle No": item.regNo,
            "Status": item.status || "N/A",
            "Last GRN No": item.grnNo || "N/A",
            "Last Assignment": item.assignmentNo || "N/A",
            "Last Active": item.lastActive ? moment(item.lastActive).format("YYYY-MM-DD HH:mm") : "N/A",
            "Idle Days": item.idleDays,
            "Client": item.client,
            "Sline": item.sline,
            "POR": item.por,
            "POL": item.pol,
            "POD": item.pod,
            "FPOD": item.fod,
            "Pick": item.pick,
            "Stuff": item.stuff,
            "Hand": item.hand,
          }));
          const ws = XLSX.utils.json_to_sheet(excelData);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Idle Vehicles");
          XLSX.writeFile(wb, `Idle_Vehicles_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
        }
      } catch (e) {
        console.error("Export failed:", e);
      } finally {
        this.isDrilldownExporting = false;
      }
    },
    async fetchMaintenanceHistory() {
      this.isMaintenanceLoading = true;
      try {
        const params = {
          startDate: moment(this.dateRange.startDate).format("YYYY-MM-DD"),
          endDate: moment(this.dateRange.endDate).format("YYYY-MM-DD"),
          vehicleTruckId: this.selectedVehicleId || undefined
        };
        const res = await VehiclePerformanceService.getMaintenanceHistory(params);
        if (res.data.status) {
          this.maintenanceHistory = res.data.data;
        }
      } catch (error) {
        console.error("Error fetching maintenance history:", error);
      } finally {
        this.isMaintenanceLoading = false;
      }
    },
    async fetchMaintenanceItemBrief() {
      if (!this.selectedVehicleId) {
        this.maintenanceItemBrief = [];
        return;
      }
      this.isItemBriefLoading = true;
      try {
        const params = {
          startDate: moment(this.dateRange.startDate).format("YYYY-MM-DD"),
          endDate: moment(this.dateRange.endDate).format("YYYY-MM-DD"),
          vehicleTruckId: this.selectedVehicleId
        };
        const res = await VehiclePerformanceService.getMaintenanceItemBrief(params);
        if (res.data.status) {
          this.maintenanceItemBrief = res.data.data;
        }
      } catch (error) {
        console.error("Error fetching item brief:", error);
      } finally {
        this.isItemBriefLoading = false;
      }
    },
    handleTabChange(newTab) {
      if (newTab === 1) {
        this.fetchOperationsIntelligence();
      }
      if (newTab === 2) {
        this.fetchLocationIntelligence();
      }
      if (newTab === 3) {
        this.fetchRevenueBreakdown();
        this.fetchAllRevenueBreakdowns();
        this.fetchAllExpenseBreakdowns();
      }
      if (newTab === 4) {
        this.fetchMaintenanceHistory();
        this.fetchMaintenanceItemBrief();
      }
    },
    refreshData() {
      this.fetchSummaryData();
      this.fetchExpenseCategoryBreakdown();
      this.fetchVehicleUtilization();
      this.fetchMostUsedVehicles();
      this.fetchIdleVehicles();
      this.fetchTrendData();
      
      // Update data based on active tab
      if (this.activeTab === 1) {
        this.fetchOperationsIntelligence();
      }
      if (this.activeTab === 2) {
        this.fetchLocationIntelligence();
      }
      if (this.activeTab === 3) {
        this.fetchAllRevenueBreakdowns();
        this.fetchAllExpenseBreakdowns();
      }
      if (this.activeTab === 4) {
        this.fetchMaintenanceHistory();
        this.fetchMaintenanceItemBrief();
      }
    },
    async fetchMostUsedVehicles() {
      try {
        const res = await VehiclePerformanceService.getMostUsedVehicles(this.utilizationDateParams());
        if (res.data && res.data.status) {
          this.topVehicles = res.data.data || [];
        }
      } catch (e) {
        console.error("Failed to fetch most used vehicles:", e);
      }
    },
    async fetchIdleVehicles() {
      try {
        const res = await VehiclePerformanceService.getIdleVehicles();
        if (res.data && res.data.status) {
          const apiData = res.data.data;
          const items = Array.isArray(apiData) ? apiData : (apiData.items || []);
          this.idleVehicles = items.map(v => Object.assign({}, v, {
            lastActive: v.lastActive ? moment(v.lastActive).format('YYYY-MM-DD') : 'N/A'
          }));
        }
      } catch (e) {
        console.error("Failed to fetch idle vehicles:", e);
      }
    },
    getStatusVariant(vehicle) {
      // console.log('vehicle:', vehicle);
      const statusColor = (vehicle && vehicle.statusColor ? String(vehicle.statusColor) : "").toLowerCase();
      // console.log('statusColor:', statusColor);
      if (statusColor) {
        return statusColor;
      }

      const status = (vehicle && vehicle.status ? String(vehicle.status) : "").toLowerCase();
      if (status === "available") return "success";
      if (status === "booked") return "danger";
      if (status === "unknown") return "secondary";
      if (status === "inactive") return "dark";
      return "primary";
    },
    showServiceDetail(record) {
      console.log('showServiceDetail called for record:', record);
      this.selectedService = Object.assign({}, record, {
        complaint: 'Unusual vibrations at 60 km/h, check suspension and brake alignment.',
        estimatedCost: '8,200'
      });
      this.showServiceModal = true;
    },
    downloadEstimate(record) {
      alert(`Generating PDF Estimate for Ticket #${record.id}...`);
    },
    goToMaintenanceModule(record) {
      if (record.gateId && record.maintId) {
        this.$sessionsStorage.set("editGateInId", record.gateId);
        this.$sessionsStorage.set("editMaintainenceRepId", record.maintId);
        this.$router.push({ path: "/yard/maintenance" });
      } else {
        alert("Maintenance session context not found for this record.");
      }
    },
    scrollToDetails() {
      const el = document.getElementById('details-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },

    exportUtilizationToExcel() {
      if (!this.filteredVehicleUtilization || !this.filteredVehicleUtilization.length) return;
      this.isUtilizationExporting = true;
      
      try {
        const fields = this.utilizationFields.filter(f => f.key !== 'actions');
        const rows = this.filteredVehicleUtilization.map(item => {
          const row = {};
          fields.forEach(field => {
            const rawVal = item[field.key];
            let val = rawVal;

            if (field.key === 'performance') {
              val = rawVal != null ? rawVal + '%' : '0%';
            } else if (['revenue', 'expense', 'pendingFineImposed', 'disposedFineImposed'].includes(field.key)) {
               val = rawVal != null ? Number(rawVal) : 0; // Number for Excel
            } else if (field.formatter && typeof field.formatter === 'function') {
              val = field.formatter(rawVal, field.key, item);
            } else if (rawVal == null) {
              val = "";
            }

            row[field.label || field.key] = val;
          });
          return row;
        });

        const fileName = `vehicle-utilization-overview-${moment().format('YYYY-MM-DD-HHmm')}`;
        this.exportRowsToXlsx(null, rows, fileName);
      } catch (error) {
        console.error("Utilization Export failed:", error);
        this.$bvToast.toast("Export failed. Please try again.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isUtilizationExporting = false;
      }
    },


    async exportDrilldownToExcel() {
      // Relaxed check: Only abort if type is not idle and we specifically have no idea what vehicle is (should not happen)
      if (this.utilDrillType !== 'idle' && this.selectedVehicleForDrilldown === undefined) {
        console.warn("Export aborted: Selected vehicle is undefined.");
        return;
      }
      this.isDrilldownExporting = true;
      
      try {
        const params = Object.assign({}, this.utilizationDateParams(), { 
          vehicleTruckId: this.selectedVehicleForDrilldown, // Can be null for All Fleet
          pageSize: -1 
        });

        console.log(`Starting full export for ${this.utilDrillType}:`, params);

        let res;
        if (this.utilDrillType === "trips") {
          res = await VehiclePerformanceService.getUtilizationTripDetails(params);
        } else {
          res = await VehiclePerformanceService.getUtilizationAssignmentDetails(params);
        }

        if (!res.data || !res.data.status) {
          throw new Error(res.data.message || "Failed to fetch full data for export.");
        }

        const allItems = res.data.data || [];
        console.log(`Export data fetched: ${allItems.length} records.`);
        
        if (!allItems.length) {
          this.$bvToast.toast("No records found to export.", { variant: "warning", solid: true });
          return;
        }

        const headers = this.utilDrillModalFields.map(f => f.label || f.key);
        
        const rows = allItems.map((item, idx) => {
          const row = {};
          this.utilDrillModalFields.forEach(field => {
            let val;
            if (field.key === 'index') {
              val = idx + 1;
            } else {
              const rawVal = item[field.key];
              val = rawVal;
              const isDateField = /Date|Time|CreatedAt|LoginTime/i.test(field.key);

              if (isDateField && rawVal && rawVal !== "0000-00-00 00:00:00") {
                const m = moment(rawVal);
                if (m.isValid()) val = m.toDate();
              } else if (field.formatter && typeof field.formatter === 'function') {
                val = field.formatter(rawVal, field.key, item);
              }
            }
            row[field.label || field.key] = val;
          });
          return row;
        });

        // Better slugification for filename (handles em-dash and other chars)
        const cleanTitle = this.utilDrillModalTitle.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').toLowerCase();
        const fileName = `${cleanTitle}-${this.selectedVehicleRegNo || 'all'}`;
        
        this.exportRowsToXlsx(null, rows, fileName);
      } catch (error) {
        console.error("Export failed:", error);
        this.$bvToast.toast("Export failed. Please try again.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isDrilldownExporting = false;
      }
    },

    exportRowsToXlsx(headers, rows, fileName) {
      const data = [];
      if (rows && rows.length) {
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const obj = {};
          const keys = headers || Object.keys(row);
          for (let j = 0; j < keys.length; j++) {
            const key = keys[j];
            obj[key] = row[key];
          }
          data.push(obj);
        }
      }
      const ws = XLSX.utils.json_to_sheet(data, { cellDates: true });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const ts = moment().format("YYYYMMDD_HHmmss");
      const finalName =
        (fileName || "vehicle-performance-export").replace(".xlsx", "") +
        "_" +
        ts +
        ".xlsx";
      XLSX.writeFile(wb, finalName);
    },
    selectVehicle(regNo) {
      this.selectedVehicleRegNo = regNo;
      
      // Find the ID for this registration number
      const vehicle = this.vehicleUtilization.find(v => v.regNo === regNo);
      this.selectedVehicleId = vehicle ? vehicle.vehicleTruckId : null;
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.refreshData();
    },
    clearVehicleSelection() {
      this.selectedVehicleRegNo = null;
      this.selectedVehicleId = null;
      this.refreshData();
    },
    async fetchLocationIntelligence() {
      this.isLocationTripLoading = true;
      this.isLocationDensityLoading = true;
      try {
        const params = this.utilizationDateParams();
        const response = await VehiclePerformanceService.getLocationIntelligence(
          params.startDate, 
          params.endDate, 
          this.selectedLocationRole
        );
        if (response.data && response.data.status) {
          const { tripTrends, density } = response.data.data;
          
          this.updateLocationTrendsChart(tripTrends);
          this.locationDensity = density || [];
        }
      } catch (e) {
        console.error("fetchLocationIntelligence error:", e);
      } finally {
        this.isLocationTripLoading = false;
        this.isLocationDensityLoading = false;
      }
    },
    onLocationRoleChanged(role) {
      this.selectedLocationRole = role;
      this.fetchLocationIntelligence();
    },
    updateLocationTrendsChart(data) {
      if (!data || !Array.isArray(data)) return;
      
      const tripData = data.slice(0, 10);
      this.tripTrendsSeries = [{
        name: 'Trips',
        data: tripData.map(d => Number(d.tripCount))
      }];
      
      this.tripTrendsOptions = Object.assign({}, this.tripTrendsOptions, {
        xaxis: {
          categories: tripData.map(d => d.location),
        }
      });
    },
  }
};
</script>

<style scoped>
.vehicle-dashboard {
  padding: 2.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.bg-light-primary { background-color: #e9ecef !important; color: #3b82f6 !important; }
.bg-light-danger { background-color: #fee2e2 !important; color: #ef4444 !important; }

.glass-input {
  background: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.dashboard-header-left {
  min-width: 0;
}

.dashboard-controls {
  flex-shrink: 0;
  gap: 8px;
}

.dashboard-date-group {
  min-width: 340px;
}

.dashboard-date-group label {
  margin-bottom: 6px;
  font-weight: 600;
}

.dashboard-refresh-btn {
  height: 38px;
  margin-bottom: 1px;
}

.dashboard-date-group ::v-deep .reportrange-text {
  min-height: 38px;
  display: flex;
  align-items: center;
}

.xs-font {
  font-size: 0.65rem;
  letter-spacing: 0.08em;
}

.stats-ticker {
  background: #e9ecef44;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #dee2e666;
}

.ticker-item {
  font-size: 0.8rem;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.vehicle-dashboard {
  animation: fadeIn 0.5s ease-in;
}

@media print {
    .dashboard-header, .kpi-summary, .charts-section, .b-tabs {
        display: none;
    }
}

@media (max-width: 1200px) {
  .dashboard-header {
    flex-wrap: wrap;
    gap: 12px;
  }

  .dashboard-controls {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .dashboard-date-group {
    min-width: 0;
    width: 100%;
    margin-right: 0 !important;
  }

  .dashboard-controls {
    align-items: stretch !important;
    flex-wrap: wrap;
  }
}
</style>



// WEBPACK FOOTER //
// VehiclePerformanceDash.vue?c88b23f8